import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  mgcIconAlertCuteFi,
  mgcIconCheckCircleFilled,
  mgcIconCloseCircleFilled,
  mgcIconCloseRegular,
  mgcIconInformationFilled,
} from '@sb/mgc-icons';

import { IconButtonComponent } from '../button';
import { Icon, IconComponent, IconRegistry } from '../icon';
import { ToastAction, ToastMessage, ToastTemplate, ToastTitle, ToastType } from './toast.model';

const statusIconMap: Record<ToastType, Icon | undefined> = {
  success: 'check-circle-filled',
  danger: 'close-circle-filled',
  warning: 'alert-cute-fi',
  info: 'information-filled',
  neutral: undefined,
};

const statusClassMap: Record<ToastType, string> = {
  success: 'toast-success',
  danger: 'toast-danger',
  warning: 'toast-warning',
  info: 'toast-info',
  neutral: '',
};

@Component({
  selector: 'sb-toast',
  standalone: true,
  imports: [NgTemplateOutlet, IconComponent, IconButtonComponent, TranslateModule],
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'aria-live': 'polite',
    '[class]': 'statusClassMap[type()]',
  },
})
export class ToastComponent {
  readonly statusClassMap = statusClassMap;

  readonly close = output();

  readonly message = input.required<ToastMessage>();

  readonly template = input<ToastTemplate>();

  readonly title = input<ToastTitle>();

  readonly type = input<ToastType>('neutral');

  readonly icon = computed(() => statusIconMap[this.type() || 'neutral']);

  readonly actions = input<ToastAction[]>();

  readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  onClose(): void {
    this.close.emit();
  }

  constructor() {
    inject(IconRegistry).registerIcons([
      mgcIconCheckCircleFilled,
      mgcIconCloseCircleFilled,
      mgcIconAlertCuteFi,
      mgcIconInformationFilled,
      mgcIconCloseRegular,
    ]);
  }
}
